import type { ModelCapabilityMap, PropertyOption, PropertySchema } from '../types';

export interface RecordParameterField {
  name: string;
  schema: PropertySchema;
  value: unknown;
  hasValue: boolean;
}

export interface RecordParameterOption {
  label: string;
  value: string | number;
}

const MAX_SLIDER_OPTION_COUNT = 1_000;

const hasOwn = (value: Record<string, unknown>, name: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, name);

export function hasRecordReferenceTemplate(
  inputs: Record<string, unknown>,
  capabilities: ModelCapabilityMap,
  mediaType: number,
): boolean {
  const model = inputs.model;
  if (typeof model !== 'string' || !hasOwn(capabilities, model)) return false;

  const templates = capabilities[model].referenceSlot?.templates;
  return Boolean(templates && Object.prototype.hasOwnProperty.call(templates, mediaType));
}

const normalizePropertyOption = (option: PropertyOption | string): RecordParameterOption =>
  typeof option === 'string'
    ? { label: option, value: option }
    : { label: option.displayName, value: option.value };

const getDecimalPlaces = (value: number): number => {
  const [coefficient, exponentText] = String(value).toLowerCase().split('e');
  const decimalPlaces = coefficient.split('.')[1]?.length ?? 0;
  const exponent = exponentText ? Number(exponentText) : 0;

  return Math.max(0, decimalPlaces - exponent);
};

const buildSliderOptions = (field: PropertySchema): RecordParameterOption[] => {
  const { min, max, step, unit = '' } = field;
  if (
    typeof min !== 'number' ||
    !Number.isFinite(min) ||
    typeof max !== 'number' ||
    !Number.isFinite(max) ||
    typeof step !== 'number' ||
    !Number.isFinite(step) ||
    step <= 0 ||
    min > max
  ) {
    return [];
  }

  const precision = Math.max(getDecimalPlaces(min), getDecimalPlaces(max), getDecimalPlaces(step));
  const scale = 10 ** precision;
  const scaledMin = Math.round(min * scale);
  const scaledMax = Math.round(max * scale);
  const scaledStep = Math.round(step * scale);

  if (![scale, scaledMin, scaledMax, scaledStep].every(Number.isSafeInteger) || scaledStep <= 0) {
    return [];
  }

  const scaledRange = scaledMax - scaledMin;
  if (!Number.isSafeInteger(scaledRange)) {
    return [];
  }

  const optionCount = Math.floor(scaledRange / scaledStep) + 1;
  if (
    !Number.isSafeInteger(optionCount) ||
    optionCount <= 0 ||
    optionCount > MAX_SLIDER_OPTION_COUNT
  ) {
    return [];
  }

  return Array.from({ length: optionCount }, (_, index) => {
    const value = (scaledMin + scaledStep * index) / scale;
    return { label: `${value}${unit}`, value };
  });
};

export function isFixedSliderValue(field: PropertySchema, value: unknown): value is number {
  return (
    field.component === 'slider' &&
    typeof value === 'number' &&
    Number.isFinite(value) &&
    typeof field.min === 'number' &&
    Number.isFinite(field.min) &&
    typeof field.max === 'number' &&
    Number.isFinite(field.max) &&
    field.min <= field.max &&
    value >= field.min &&
    value <= field.max
  );
}

const hasMatchingPropertyOption = (field: PropertySchema, value: unknown): boolean =>
  field.enum?.some((option) =>
    Object.is(typeof option === 'string' ? option : option.value, value),
  ) ?? false;

export function isPropertyValueSupported(field: PropertySchema, value: unknown): boolean {
  if (value === undefined || value === null) return false;

  if (hasMatchingPropertyOption(field, value)) return true;

  switch (field.component) {
    case 'text':
    case 'textarea':
      return typeof value === 'string';
    case 'singleSelect':
    case 'singleButton':
      return !field.enum && (typeof value === 'string' || typeof value === 'number');
    case 'numberInput':
    case 'slider':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        (field.min === undefined || value >= field.min) &&
        (field.max === undefined || value <= field.max)
      );
    case 'switch':
      return typeof value === 'boolean';
    default:
      return false;
  }
}

export function getSliderSpecialOptions(field: PropertySchema): RecordParameterOption[] {
  if (field.component !== 'slider' || !field.enum) return [];

  return field.enum
    .map(normalizePropertyOption)
    .filter(
      (option) =>
        typeof option.value === 'number' &&
        Number.isFinite(option.value) &&
        !isFixedSliderValue(field, option.value),
    );
}

export function canReusePropertyValueAcrossModels(field: PropertySchema, value: unknown): boolean {
  if (field.component === 'slider' && getSliderSpecialOptions(field).length > 0) return false;
  return isPropertyValueSupported(field, value);
}

export function resolveFixedSliderValue(
  field: PropertySchema,
  currentValue: unknown,
  rememberedValue?: number,
): number {
  const candidates = [currentValue, rememberedValue, field.default, field.min];
  const fixedValue = candidates.find((value) => isFixedSliderValue(field, value));
  if (typeof fixedValue === 'number') return fixedValue;

  if (typeof field.default === 'number' && Number.isFinite(field.default)) return field.default;
  if (typeof field.min === 'number' && Number.isFinite(field.min)) return field.min;
  return 0;
}

export function resolveRecordParameterFields(
  inputs: Record<string, unknown>,
  capabilities: ModelCapabilityMap,
): RecordParameterField[] {
  const model = inputs.model;
  if (typeof model !== 'string') {
    return [];
  }

  if (!hasOwn(capabilities, model)) {
    return [];
  }

  const capability = capabilities[model];

  return capability.fields.flatMap((name) => {
    const schema = capability.properties[name];
    if (name === 'model' || !schema || schema.group !== 'popover' || schema.capsule === false) {
      return [];
    }

    return [
      {
        name,
        schema,
        value: inputs[name],
        hasValue: hasOwn(inputs, name),
      },
    ];
  });
}

export function buildRecordParameterOptions(field: PropertySchema): RecordParameterOption[] {
  if (field.component === 'slider') {
    const options = [...getSliderSpecialOptions(field), ...buildSliderOptions(field)];
    return options.filter(
      (option, index) =>
        options.findIndex((candidate) => Object.is(candidate.value, option.value)) === index,
    );
  }

  if (field.enum) {
    return field.enum.map(normalizePropertyOption);
  }

  return [];
}

export function formatRecordParameterValue(field: RecordParameterField): string {
  if (!field.hasValue) {
    return '-';
  }

  const matchingOption = buildRecordParameterOptions(field.schema).find((option) =>
    Object.is(option.value, field.value),
  );
  if (matchingOption) {
    return matchingOption.label;
  }

  if (typeof field.value === 'number') {
    return `${field.value}${field.schema.unit ?? ''}`;
  }

  return String(field.value);
}

export function haveRecordParameterValuesChanged(
  initialInputs: Record<string, unknown>,
  currentInputs: Record<string, unknown>,
  fieldNames: string[],
): boolean {
  return fieldNames.some((name) => {
    const initialHasValue = hasOwn(initialInputs, name);
    const currentHasValue = hasOwn(currentInputs, name);

    if (initialHasValue !== currentHasValue) {
      return true;
    }

    if (!initialHasValue) {
      return false;
    }

    return !Object.is(initialInputs[name], currentInputs[name]);
  });
}
